import { useState } from "react";
import { useAuth } from "@/lib/auth";
import AppShell from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";

export default function Profile() {
  const { user, profile, refreshProfile, updatePassword } = useAuth();
  const [form, setForm] = useState({
    full_name: profile?.full_name || user?.user_metadata?.full_name || "",
  });
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const displayName = profile?.full_name || user?.user_metadata?.full_name || "";
  const displayEmail = user?.email || "";
  const initials = (displayName || "?").split(" ").map(s=>s[0]).join("").slice(0,2).toUpperCase();
  const memberSince = user?.created_at ? new Date(user.created_at) : null;

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Update auth user metadata + public.profiles row (RLS: users can update their own).
      const { error: metaErr } = await supabase.auth.updateUser({ data: { full_name: form.full_name } });
      if (metaErr) throw metaErr;
      const { error: profErr } = await supabase
        .from("profiles")
        .update({ full_name: form.full_name })
        .eq("id", user.id);
      if (profErr) throw profErr;
      await refreshProfile();
      toast.success("Profile updated");
    } catch (err) { toast.error(err?.message || "Failed"); }
    finally { setSaving(false); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) return toast.error("New password must be at least 8 characters");
    setSaving(true);
    try {
      await updatePassword(newPassword);
      setNewPassword("");
      toast.success("Password updated");
    } catch (err) { toast.error(err?.message || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <AppShell subtitle="§ ACCOUNT" title="Profile">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-5xl">
        <div className="lg:col-span-1">
          <div className="border border-[#DCE3ED] p-8 text-center rounded-xl bg-white">
            <div className="w-20 h-20 rounded-md bg-[#0B4DCC] text-white text-2xl font-semibold flex items-center justify-center mx-auto">{initials}</div>
            <div className="mt-4 font-semibold text-lg tracking-tight">{displayName || "—"}</div>
            <div className="text-xs text-[#64748B] font-mono break-all">{displayEmail}</div>
            {memberSince && (
              <div className="mt-4 pt-4 border-t border-[#DCE3ED] text-xs">
                <div className="fx-mono-tag mb-1">MEMBER SINCE</div>
                <div>{memberSince.toLocaleDateString()}</div>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <form onSubmit={saveProfile} className="border border-[#DCE3ED] p-8 rounded-xl bg-white" data-testid="profile-form">
            <div className="fx-mono-tag mb-6">PERSONAL INFO</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <Label className="fx-mono-tag">FULL NAME</Label>
                <Input value={form.full_name} onChange={e=>setForm({...form, full_name:e.target.value})} className="mt-2 h-11 rounded-md" data-testid="profile-name-input" />
              </div>
              <div className="sm:col-span-2">
                <Label className="fx-mono-tag">EMAIL</Label>
                <Input value={displayEmail} disabled className="mt-2 h-11 rounded-md bg-[#F4F6F8]" data-testid="profile-email-input" />
                <p className="mt-2 text-xs text-[#64748B]">Contact support to change your email.</p>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <Button disabled={saving} className="fx-btn-primary rounded-lg h-11 px-6" data-testid="profile-save-btn">
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </form>

          <form onSubmit={changePw} className="border border-[#DCE3ED] p-8 rounded-xl bg-white" data-testid="password-form">
            <div className="fx-mono-tag mb-6">CHANGE PASSWORD</div>
            <Label className="fx-mono-tag">NEW PASSWORD</Label>
            <Input required type="password" autoComplete="new-password" value={newPassword} onChange={e=>setNewPassword(e.target.value)} className="mt-2 h-11 rounded-md" placeholder="8+ characters" data-testid="pw-new-input" />
            <div className="mt-6 flex justify-end">
              <Button disabled={saving} className="fx-btn-primary rounded-lg h-11 px-6" data-testid="pw-save-btn">
                {saving ? "Updating…" : "Update password"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </AppShell>
  );
}
